import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { usePortfolio } from '@/lib/portfolio';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Building2, Info, Plus, GitBranch, FlaskConical, Target, ShieldAlert, FileText, ArrowUpRight, ArrowDownRight } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const fmtCompact = (v) => { const abs = Math.abs(v); if (abs >= 1e6) return `${(v/1e6).toFixed(1)}M`; if (abs >= 1e3) return `${(v/1e3).toFixed(0)}k`; return v.toFixed(0); };
const fmtKpi = (v) => { const abs = Math.abs(v); return abs >= 1e6 ? `€ ${(v/1e6).toFixed(2)}M` : fmt(v); };
const ct = { background: '#fff', border: '1px solid #E2DDD3', borderRadius: 8, fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', padding: '8px 12px' };
const sColor = (v, good, warn) => v >= good ? '#2E6F57' : v >= warn ? '#A67C52' : '#8A3C3C';
const COLORS = ['#1F3A34', '#9C9488', '#D9D3C8', '#285047', '#A67C52'];
function mp(p, r, y) { if (r === 0) return y > 0 ? p / (y * 12) : 0; const rm = r / 12, n = y * 12; return p * (rm * Math.pow(1 + rm, n)) / (Math.pow(1 + rm, n) - 1); }

function MiniSparkline({ data, color = '#1F3A34', w = 60, h = 22 }) {
  if (!data?.length) return null;
  const min = Math.min(...data), max = Math.max(...data), range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * (h - 4) - 2}`).join(' ');
  return <svg width={w} height={h} className="inline-block"><polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

export default function Home() {
  const navigate = useNavigate();
  const { assets, loading: pfLoading } = usePortfolio();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (pfLoading) return;
    setLoading(true);
    api.get('/contracts').then(({ data }) => setContracts(data)).catch(console.error).finally(() => setLoading(false));
  }, [pfLoading, assets]);

  const d = useMemo(() => {
    if (!assets.length) return null;
    let tv = 0, td = 0, tNoi = 0, tAds = 0, tCf = 0;
    assets.forEach(a => { const m = a.metrics || {}; tv += m.market_value || 0; td += m.loan_amount || 0; tNoi += m.noi || 0; tAds += m.annual_debt_service || 0; tCf += m.net_cashflow || 0; });
    const ltv = tv > 0 ? td / tv * 100 : 0, dscr = tAds > 0 ? tNoi / tAds : 0, ny = tv > 0 ? tNoi / tv * 100 : 0;
    const avgVac = assets.reduce((s, a) => s + (a.vacancy_rate || 0), 0) / assets.length;
    const pirr = (() => { const tw = assets.reduce((s, a) => s + Math.max((a.metrics?.equity || 0), 1), 0); return assets.reduce((s, a) => s + (a.metrics?.irr || 0) * Math.max((a.metrics?.equity || 0), 1) / tw, 0); })();

    // Scores
    const stratScore = Math.round(Math.min(100, (dscr >= 1.25 ? 18 : 8) + (ltv <= 65 ? 16 : 6) + (pirr >= 12 ? 18 : 8) + (ny >= 4 ? 16 : 6) + 14 + 12));
    const riskScore = Math.round(assets.reduce((s, a) => { const m = a.metrics || {}; return s + Math.min(100, Math.max(0, 80 - (m.dscr || 0) * 30 + ((a.loan?.interest_rate || 0) > 4 ? 15 : 0))); }, 0) / assets.length);

    // Sparklines (mock 12 months)
    const cfSpark = Array.from({ length: 12 }, (_, i) => tCf / 12 * (0.92 + i * 0.008 + Math.sin(i) * 0.015));
    const irrSpark = Array.from({ length: 12 }, (_, i) => pirr * (0.96 + i * 0.004));

    // Segments
    const segs = {}; const totalRent = assets.reduce((s, a) => s + (a.annual_rent || 0), 0);
    assets.forEach(a => { const t = (a.property_type || 'residential'); segs[t] = (segs[t] || 0) + (a.annual_rent || 0); });
    const segData = Object.entries(segs).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, pct: totalRent > 0 ? Math.round(v / totalRent * 100) : 0 }));

    // Cities
    const cities = {}; assets.forEach(a => { const c = a.city || '?'; cities[c] = (cities[c] || 0) + (a.metrics?.market_value || 0); });
    const cityData = Object.entries(cities).map(([k, v]) => ({ name: k, value: v, pct: tv > 0 ? Math.round(v / tv * 100) : 0 })).sort((a, b) => b.value - a.value);

    // History
    const now = new Date(), hist = [];
    for (let m = 60; m >= 0; m--) {
      const dt = new Date(now); dt.setDate(dt.getDate() - m * 30);
      const f = Math.pow(1.04, (60 / 12 - m / 12)) / Math.pow(1.04, 5);
      let dip = 1; if (m >= 26 && m <= 36) dip = 0.94 + 0.06 * Math.abs(m - 31) / 5;
      const factor = f * dip;
      hist.push({ date: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`, value: Math.round(tv * factor), equity: Math.round((tv - td) * factor * 1.02) });
    }

    // Cashflow 12m
    const cf12 = Array.from({ length: 12 }, (_, i) => ({ month: i + 1, cf: Math.round(tCf / 12 * (1 + i * 0.003)) }));

    // LTV + DSCR trend
    const covTrend = Array.from({ length: 10 }, (_, i) => ({ year: (2021 + i).toString(), ltv: +(ltv * (1.04 - i * 0.005)).toFixed(1), dscr: +(dscr * (0.92 + i * 0.018)).toFixed(2) }));

    // Tenant top 3
    const tenantRent = {};
    contracts.forEach(c => { tenantRent[c.tenant_name || '?'] = (tenantRent[c.tenant_name || '?'] || 0) + (c.monthly_rent || 0) * 12; });
    const tenants = Object.entries(tenantRent).map(([k, v]) => ({ name: k, pct: totalRent > 0 ? +(v / totalRent * 100).toFixed(0) : 0 })).sort((a, b) => b.pct - a.pct).slice(0, 3);

    // WALT
    const walt = contracts.length > 0 ? contracts.reduce((s, c) => { const mo = Math.max(0, Math.round((new Date(c.end_date) - new Date()) / (30.44*24*60*60*1000))); return s + mo * (c.monthly_rent || 0); }, 0) / Math.max(contracts.reduce((s, c) => s + (c.monthly_rent || 0), 0), 1) / 12 : 0;

    // Expiry ladder
    const exYears = {};
    contracts.forEach(c => { const yr = c.end_date?.substring(0, 4) || '?'; if (!exYears[yr]) exYears[yr] = { year: yr, rent: 0 }; exYears[yr].rent += (c.monthly_rent || 0) * 12; });
    const expiryData = Object.values(exYears).sort((a, b) => a.year.localeCompare(b.year));

    // Maturity ladder (mock)
    const maturity = [{ year: '2025', amount: 248000 }, { year: '2026', amount: 520000 }, { year: '2027', amount: 390000 }, { year: '2028', amount: 0 }, { year: '2029', amount: 994000 }, { year: '2030+', amount: 2415000 }];

    // Liq
    const liqMonths = tAds > 0 ? Math.round(435000 / (tAds / 12)) : 99;

    // Top 5 assets sorted by value
    const topAssets = [...assets].map(a => ({ ...a, m: a.metrics || {} })).sort((a, b) => (b.m.market_value || 0) - (a.m.market_value || 0)).slice(0, 5);

    return { tv, td, tCf, ltv, dscr, ny, pirr, avgVac, stratScore, riskScore, liqMonths,
      cfSpark, irrSpark, segData, cityData, hist, cf12, covTrend, tenants, walt, expiryData, maturity, topAssets, assetCount: assets.length };
  }, [assets, contracts]);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><p className="text-[14px] text-[#8C877F] animate-pulse">Loading...</p></div>;
  if (!d) return <div className="flex flex-col items-center justify-center h-[60vh] text-center"><Building2 className="w-10 h-10 text-[#1F3A34]/25 mb-4" /><h2 className="font-serif text-[32px] text-[#1C1C1C]">Welcome</h2><p className="text-[14px] text-[#8C877F] mt-2">Set up your portfolio to get started.</p></div>;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Hero KPIs */}
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
        {[
          { l: 'Portfolio Value', v: fmtKpi(d.tv), tip: 'Total market value of all assets' },
          { l: 'Total Equity', v: fmtKpi(d.tv - d.td), tip: 'Value minus debt' },
          { l: 'Net CF/mo', v: fmtKpi(d.tCf / 12), c: d.tCf >= 0 ? '#2E6F57' : '#8A3C3C', spark: d.cfSpark, tip: 'Monthly net cashflow after debt service' },
          { l: 'IRR', v: `${d.pirr.toFixed(1)}%`, c: sColor(d.pirr, 10, 8), spark: d.irrSpark, tip: 'Weighted internal rate of return' },
          { l: 'LTV', v: `${d.ltv.toFixed(1)}%`, c: sColor(100 - d.ltv, 35, 30), tip: 'Loan-to-value ratio' },
          { l: 'DSCR', v: d.dscr.toFixed(2), c: sColor(d.dscr, 1.25, 1.1), tip: 'Debt service coverage ratio' },
          { l: 'Vacancy', v: `${d.avgVac.toFixed(1)}%`, c: sColor(5 - d.avgVac, 2, 0), tip: 'Average vacancy rate' },
          { l: 'Strategy', v: `${d.stratScore}`, c: sColor(d.stratScore, 70, 50), tip: 'Strategy health score' },
          { l: 'Risk', v: `${d.riskScore}`, c: sColor(100 - d.riskScore, 60, 40), tip: 'Portfolio risk score' },
        ].map(k => (
          <div key={k.l} className="bg-white border border-[#E2DDD3] rounded-xl p-3" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <div className="flex items-center gap-1 mb-1">
              <p className="text-[9px] text-[#8C877F] font-sans font-medium">{k.l}</p>
              <TooltipProvider delayDuration={200}><Tooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-[#D9D3C8] cursor-help" /></TooltipTrigger><TooltipContent className="text-xs font-sans max-w-[160px]">{k.tip}</TooltipContent></Tooltip></TooltipProvider>
            </div>
            <p className="text-[15px] font-sans font-semibold leading-none truncate" style={{ color: k.c || '#1C1C1C' }}>{k.v}</p>
            {k.spark && <div className="mt-1.5"><MiniSparkline data={k.spark} color={k.c || '#1F3A34'} /></div>}
          </div>
        ))}
      </div>

      {/* Core Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Value & Equity */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-4">Portfolio Value & Equity — 5 Year</p>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={d.hist}>
              <defs><linearGradient id="hEq" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1F3A34" stopOpacity={0.1} /><stop offset="95%" stopColor="#1F3A34" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#E3DED6" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} interval={11} />
              <YAxis tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1e6).toFixed(1)}M`} width={42} />
              <RTooltip contentStyle={ct} formatter={v => [fmt(v), '']} />
              <Area type="monotone" dataKey="value" stroke="#9C9488" strokeWidth={1} fill="none" />
              <Area type="monotone" dataKey="equity" stroke="#1F3A34" strokeWidth={1.8} fill="url(#hEq)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Cashflow */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-4">Monthly Net Cashflow — 12 Months</p>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={d.cf12}>
              <defs><linearGradient id="hCf" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1F3A34" stopOpacity={0.08} /><stop offset="95%" stopColor="#1F3A34" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#E3DED6" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1e3).toFixed(0)}k`} />
              <RTooltip contentStyle={ct} formatter={v => [fmt(v), '']} />
              <Area type="monotone" dataKey="cf" stroke="#1F3A34" strokeWidth={1.8} fill="url(#hCf)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Allocation */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-4">Asset Allocation</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-4">
              <ResponsiveContainer width={100} height={100}>
                <PieChart><Pie data={d.segData} cx="50%" cy="50%" innerRadius={30} outerRadius={45} dataKey="value" strokeWidth={0}>
                  {d.segData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie></PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5">
                {d.segData.map((s, i) => <div key={s.name} className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-sm" style={{ backgroundColor: COLORS[i] }} /><span className="text-[11px] text-[#1C1C1C] font-sans">{s.name} <strong>{s.pct}%</strong></span></div>)}
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="text-[10px] text-[#8C877F] font-sans font-medium mb-1">By City</p>
              {d.cityData.slice(0, 5).map(c => (
                <div key={c.name} className="flex items-center gap-2">
                  <span className="text-[11px] text-[#5A5A5A] w-20 font-sans">{c.name}</span>
                  <div className="flex-1 h-1.5 bg-[#F4F1EB] rounded-full"><div className="h-full rounded-full bg-[#1F3A34]" style={{ width: `${c.pct}%` }} /></div>
                  <span className="text-[11px] font-sans font-semibold w-8 text-right">{c.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Leverage & Coverage */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-4">Leverage & Coverage Trend</p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={d.covTrend}>
              <CartesianGrid stroke="#E3DED6" vertical={false} />
              <XAxis dataKey="year" tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} domain={[40, 80]} tickFormatter={v => `${v}%`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} domain={[0.8, 2]} />
              <RTooltip contentStyle={ct} />
              <ReferenceLine yAxisId="right" y={1.25} stroke="#8A3C3C" strokeDasharray="6 3" />
              <Line yAxisId="left" type="monotone" dataKey="ltv" stroke="#9C9488" strokeWidth={1.5} dot={false} name="LTV %" />
              <Line yAxisId="right" type="monotone" dataKey="dscr" stroke="#1F3A34" strokeWidth={1.8} dot={false} name="DSCR" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Risk Snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {/* Tenant Top 3 */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-4" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
          <p className="text-[10px] text-[#8C877F] font-sans font-medium mb-2">Top 3 Tenants</p>
          {d.tenants.map((t, i) => (
            <div key={i} className="flex items-center justify-between py-1 border-b border-[#F4F1EB] last:border-0">
              <span className="text-[11px] text-[#1C1C1C] font-sans truncate max-w-[100px]">{t.name}</span>
              <span className="text-[11px] font-sans font-semibold" style={{ color: t.pct > 25 ? '#8A3C3C' : '#1C1C1C' }}>{t.pct}%</span>
            </div>
          ))}
        </div>
        {/* WALT */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-4 flex flex-col justify-center items-center" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
          <p className="text-[10px] text-[#8C877F] font-sans font-medium">WALT</p>
          <p className="text-[28px] font-sans font-semibold" style={{ color: sColor(d.walt, 3, 1.5) }}>{d.walt.toFixed(1)}</p>
          <p className="text-[10px] text-[#8C877F]">years</p>
        </div>
        {/* Expiry */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-4" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
          <p className="text-[10px] text-[#8C877F] font-sans font-medium mb-2">Expiry Ladder</p>
          <ResponsiveContainer width="100%" height={70}>
            <BarChart data={d.expiryData}><Bar dataKey="rent" fill="#1F3A34" barSize={12} radius={[2, 2, 0, 0]} /><XAxis dataKey="year" tick={{ fill: '#8C877F', fontSize: 8 }} axisLine={false} tickLine={false} /></BarChart>
          </ResponsiveContainer>
        </div>
        {/* Maturity */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-4" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
          <p className="text-[10px] text-[#8C877F] font-sans font-medium mb-2">Debt Maturity</p>
          <ResponsiveContainer width="100%" height={70}>
            <BarChart data={d.maturity}><Bar dataKey="amount" fill="#285047" barSize={12} radius={[2, 2, 0, 0]} /><XAxis dataKey="year" tick={{ fill: '#8C877F', fontSize: 8 }} axisLine={false} tickLine={false} /></BarChart>
          </ResponsiveContainer>
        </div>
        {/* Liquidity */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-4 flex flex-col justify-center items-center" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
          <p className="text-[10px] text-[#8C877F] font-sans font-medium">Liquidity</p>
          <p className="text-[28px] font-sans font-semibold" style={{ color: sColor(d.liqMonths, 12, 6) }}>{d.liqMonths}</p>
          <p className="text-[10px] text-[#8C877F]">months runway</p>
        </div>
      </div>

      {/* Top Assets */}
      <div className="bg-white border border-[#E2DDD3] rounded-xl overflow-hidden" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <div className="px-5 py-3 border-b border-[#E2DDD3] flex items-center justify-between">
          <p className="text-[12px] text-[#8C877F] font-sans font-medium">Top Assets</p>
          <button onClick={() => navigate('/portfolio')} className="text-[11px] text-[#1F3A34] font-sans font-medium hover:underline">View All →</button>
        </div>
        <div className="grid grid-cols-8 gap-1 px-5 py-2 border-b border-[#E2DDD3] text-[9px] text-[#8C877F] font-sans font-medium uppercase tracking-wider">
          <span className="col-span-2">Asset</span><span className="text-right">Value</span><span className="text-right">IRR</span><span className="text-right">Yield</span><span className="text-right">LTV</span><span className="text-right">CF/mo</span><span className="text-right">DSCR</span>
        </div>
        {d.topAssets.map(a => (
          <div key={a.asset_id} onClick={() => navigate(`/assets/${a.asset_id}`)} className="grid grid-cols-8 gap-1 px-5 py-2.5 border-b border-[#F4F1EB] last:border-0 text-[12px] hover:bg-[#F4F1EB]/40 cursor-pointer">
            <div className="col-span-2"><p className="font-sans font-medium text-[#1C1C1C] truncate">{a.name}</p><p className="text-[10px] text-[#8C877F]">{a.city}</p></div>
            <p className="text-right font-sans font-semibold text-[#1C1C1C] self-center">{fmt(a.m.market_value || 0)}</p>
            <p className="text-right font-sans font-semibold text-[#1F3A34] self-center">{(a.m.irr || 0).toFixed(1)}%</p>
            <p className="text-right font-sans font-semibold self-center">{(a.m.net_yield || 0).toFixed(1)}%</p>
            <p className="text-right font-sans font-semibold self-center">{(a.m.ltv || 0).toFixed(0)}%</p>
            <p className={`text-right font-sans font-semibold self-center ${(a.m.net_cashflow || 0) >= 0 ? 'text-[#2E6F57]' : 'text-[#8A3C3C]'}`}>{fmt((a.m.net_cashflow || 0) / 12)}</p>
            <p className="text-right font-sans font-semibold self-center" style={{ color: sColor(a.m.dscr || 0, 1.25, 1.1) }}>{(a.m.dscr || 0).toFixed(2)}</p>
          </div>
        ))}
      </div>

      {/* Quick Access */}
      <div className="flex flex-wrap gap-2">
        {[
          { l: 'Add Property', icon: Plus, path: '/add-asset', primary: true },
          { l: 'Add Deal', icon: GitBranch, path: '/pipeline' },
          { l: 'Stress Test', icon: FlaskConical, path: '/scenarios' },
          { l: 'Strategy', icon: Target, path: '/strategy' },
          { l: 'Risk', icon: ShieldAlert, path: '/risk' },
          { l: 'Reports', icon: FileText, path: '/reports' },
        ].map(btn => (
          <button key={btn.l} onClick={() => navigate(btn.path)}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-[12px] font-sans font-medium transition-all ${btn.primary ? 'bg-[#1F3A34] text-white hover:bg-[#285047]' : 'bg-white border border-[#E2DDD3] text-[#5A5A5A] hover:border-[#1F3A34]/30 hover:text-[#1F3A34]'}`}>
            <btn.icon className="w-3.5 h-3.5" /> {btn.l}
          </button>
        ))}
      </div>
    </div>
  );
}
